// Browser-side hooks for BYOA / "Koneksi AI-ku" (docs/nontech/CONTRACT.md §Fase 1b).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '#/lib/api'
import type { AiConnectionDto, AiConnectionTestResult, AiProvider, WorkspaceAiSelection } from '#/lib/byoa'
import type { RunBackend } from '#/lib/runner/types'

export type { AiConnectionDto, AiConnectionTestResult }

export const qkAiConnections = ['ai-connections'] as const

export const useAiConnections = (enabled = true) =>
  useQuery({ queryKey: qkAiConnections, queryFn: () => api<{ connections: AiConnectionDto[] }>('/api/ai-connections'), enabled })

export const useWorkspaceAiSelection = (workspaceId: string) =>
  useQuery({ queryKey: ['workspace-ai-selection', workspaceId], queryFn: () => api<WorkspaceAiSelection>(`/api/workspace/${workspaceId}/ai-connection`) })

export interface NewConnection {
  label?: string
  provider: AiProvider
  baseUrl?: string | null
  apiKey: string
  model: string
  backendPref?: RunBackend | null
}

export function useCreateAiConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: NewConnection) => api<{ connection: AiConnectionDto }>('/api/ai-connections', { method: 'POST', json: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qkAiConnections }),
  })
}

export function useDeleteAiConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ deleted: boolean }>(`/api/ai-connections/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qkAiConnections }),
  })
}

export function useTestAiConnection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<AiConnectionTestResult>(`/api/ai-connections/${id}/test`, { method: 'POST', json: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qkAiConnections }),
  })
}

/** Which key (and which builder) this workspace uses. `aiConnectionId: null` = Bystep credits. */
export function useSetWorkspaceAiConnection(workspaceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { aiConnectionId?: string | null; stageConnectionId?: string | null; backend?: RunBackend | null }) =>
      api<{ aiConnectionId: string | null; connection: AiConnectionDto | null }>(`/api/workspace/${workspaceId}/ai-connection`, { method: 'PATCH', json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qkAiConnections })
      void qc.invalidateQueries({ queryKey: ['runner', workspaceId] })
      void qc.invalidateQueries({ queryKey: ['workspace-ai-selection', workspaceId] })
    },
  })
}
